import { useEffect, useState } from 'react';
import { FURNITURE_CATEGORIES, FURNITURE_ROOM_TYPES, FURNITURE_ROOM_LABELS } from '@/lib/furnitureCatalog';
import { listRequiredCategories, setRequiredCategories } from '@/services/moodboardService';
import type { RequiredCategoryRow } from '@/types';

export const AdminMoodboardChecklistView: React.FC = () => {
  const [roomType, setRoomType] = useState<string>(FURNITURE_ROOM_TYPES[0]);
  const [rows, setRows] = useState<RequiredCategoryRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listRequiredCategories().then(setRows).catch((e) => setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen'));
  }, []);

  const selected = new Set(rows.filter((r) => r.room_type === roomType).map((r) => r.category));

  const toggle = async (category: string) => {
    setError(null);
    const next = new Set(selected);
    if (next.has(category)) next.delete(category); else next.add(category);
    setSaving(true);
    try {
      await setRequiredCategories(roomType, [...next]);
      setRows((prev) => [
        ...prev.filter((r) => r.room_type !== roomType),
        ...[...next].map((category) => ({ room_type: roomType, category })),
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Element-Checklisten</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Pflicht-Kategorien pro Raum. Moodboards ohne diese Elemente zeigen einen Hinweis (blockiert nicht).
        </p>
      </div>
      {error && <div className="text-sm text-destructive">Fehler: {error}</div>}
      <select value={roomType} onChange={(e) => setRoomType(e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm">
        {FURNITURE_ROOM_TYPES.map((r) => (
          <option key={r} value={r}>{FURNITURE_ROOM_LABELS[r] ?? r}</option>
        ))}
      </select>
      <div className="flex flex-wrap gap-2">
        {FURNITURE_CATEGORIES.map((c) => (
          <button key={c} type="button" disabled={saving} onClick={() => toggle(c)}
            className={`rounded-full border px-3 py-1 text-xs ${selected.has(c) ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-muted text-muted-foreground'}`}>
            {c}
          </button>
        ))}
      </div>
    </div>
  );
};
